import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import {
    ApiRequestService,
    DateUtilService,
    MAT_ICON,
    Material,
    TOAST_STATE,
    ToastmessageService,
    TranslationService,
    Void
} from '@prekog/common-ui';
import { GetProductsResponse } from 'src/app/products/product-list/get-products.response';
import { SUCCESSFULLY_MODIFIED, SUCCESSFULLY_SAVED } from 'src/app/shared/adminconstants';
import { AddPartnerCommissionRequest } from './addpartnercommissionrequest';
import { AddPartnerOnSalePeriodRequest } from './addpartneronsaleperiodrequest';
import { AddPartnerRequest } from './addpartnerrequest';
import { UpdatePartnerCommissionRequest } from './updatepartnercommissionrequest';
import { UpdatePartnerOnSalePeriodRequest } from './updatepartneronsaleperiod';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-addpartner',
    templateUrl: './addpartner.component.html',
    styleUrls: ['./addpartner.component.scss'],
    standalone: true,
    imports: [ReactiveFormsModule, MatIconModule]
})
export class AddPartnerComponent implements OnInit {
    addPartner!: FormGroup;
    addPartnerCommissionUri = '/admin/addPartnerCommission';
    addPartnerOnSalePeriodUri = '/admin/addPartnerOnSalePeriod';
    addPartnerUri = '/admin/addPartner';
    commissionNeeded = false;
    commissionOnlyModeEnabled = false;
    defaultDate!: string;
    editablePartnerCommissionId!: number;
    editablePartnerOnSalePeriodId!: number;
    getProductsURI = '/admin/getProducts';
    newPartner = true;
    onSalePeriodNeeded = false;
    onSalePeriodOnlyModeEnabled = false;
    partnerName: string | null = null;
    products: Material[] = [];
    selectedMode: string | null = null;
    storeBaseUrl = inject<string>('STORE_BACKEND_URL' as never);
    updatePartnerCommissionUri = '/admin/updatePartnerCommission';
    updatePartnerOnSalePeriodUri = '/admin/updatePartnerOnSalePeriod';
    private _fb = inject(FormBuilder);

    private _router = inject(Router);

    private _route = inject(ActivatedRoute);

    private _location = inject(Location);

    private _apiRequestService = inject(ApiRequestService);

    private _toastMessageService = inject(ToastmessageService);

    private _translateService = inject(TranslationService);

    private _dateUtilService = inject(DateUtilService);

    ngOnInit(): void {
        if (!sessionStorage.getItem('token')) {
            void this._router.navigateByUrl('/');
        } else {
            this._route.queryParamMap.subscribe((params) => {
                this.selectedMode = params.get('mode');
                if (this.selectedMode === 'addCommission' || this.selectedMode === 'addOnSalePeriod') {
                    this.partnerName = params.get('partnerName');
                }
            });
            this._loadProducts();
            this._setDefaultStartDate();
            this._checkSelectedFormMode();
            this._launchFormWithSelectedMode();
        }
    }

    addParnterCommission(): void {
        this.commissionNeeded = !this.commissionNeeded;
    }

    addParnterOnSalePeriod(): void {
        this.onSalePeriodNeeded = !this.onSalePeriodNeeded;
    }

    onCancel(): void {
        this._location.back();
    }

    onSave(): void {
        if (this.newPartner) {
            this._addPartner();
        }

        if (this.commissionNeeded) {
            if (this.selectedMode === 'editCommission') {
                this._updatePartnerCommission();
            } else if (!this.newPartner) {
                this._addPartnerCommission();
            }
        }

        if (this.onSalePeriodNeeded) {
            if (this.selectedMode === 'editOnSalePeriod') {
                this._updatePartnerOnSalePeriod();
            } else if (!this.newPartner) {
                this._addPartnerOnSalePeriod();
            }
        }
    }

    private _checkSelectedFormMode(): void {
        if (this.selectedMode === 'editCommission') {
            this.commissionOnlyModeEnabled = true;
            this._buildCommissionDataForEdit();
        } else if (this.selectedMode === 'editOnSalePeriod') {
            this.onSalePeriodOnlyModeEnabled = true;
            this._buildOnSalePeriodDataForEdit();
        } else {
            if (this.selectedMode === 'addCommission') {
                this.commissionOnlyModeEnabled = true;
            } else if (this.selectedMode === 'addOnSalePeriod') {
                this.onSalePeriodOnlyModeEnabled = true;
            }
            this._buildAddPartnerForm();
        }
    }

    private _addPartner(): void {
        const addPartnerRequest: AddPartnerRequest = {
            partnerName: this.addPartner.value.partnerName
        };

        this._apiRequestService
            .sendRequest<AddPartnerRequest, Void>(this.storeBaseUrl, addPartnerRequest, true, this.addPartnerUri)
            .subscribe(() => {
                this._toastMessageService.displayToastMessage(
                    TOAST_STATE.success,
                    this._translateService.translate(SUCCESSFULLY_SAVED),
                    MAT_ICON.success
                );
                if (this.commissionNeeded) {
                    this._addPartnerCommission();
                }
                if (this.onSalePeriodNeeded) {
                    this._addPartnerOnSalePeriod();
                }
                void this._router.navigateByUrl('/partners');
            });
    }

    private _addPartnerOnSalePeriod(): void {
        const addPartnerOnSalePeriodRequest: AddPartnerOnSalePeriodRequest = {
            partnerName: this.partnerName ? this.partnerName : this.addPartner.value.partnerName,
            productIdForOnSalePeriod: this.addPartner.value.productIdForOnSalePeriod,
            onSalePeriodName: this.addPartner.value.partnerOnSalePeriodName,
            onSalePeriodPercentage: this.addPartner.value.partnerOnSalePeriodPercentage,
            onSalePeriodStartDate: this.addPartner.value.partnerOnSalePeriodStartDate,
            onSalePeriodEndDate: this.addPartner.value.partnerOnSalePeriodEndDate
        };

        this._apiRequestService
            .sendRequest<
                AddPartnerOnSalePeriodRequest,
                Void
            >(this.storeBaseUrl, addPartnerOnSalePeriodRequest, true, this.addPartnerOnSalePeriodUri)
            .subscribe(() => {
                this._toastMessageService.displayToastMessage(
                    TOAST_STATE.success,
                    this._translateService.translate(SUCCESSFULLY_SAVED),
                    MAT_ICON.success
                );
                if (!this.newPartner) {
                    void this._router.navigateByUrl('/partners');
                }
            });
    }

    private _addPartnerCommission(): void {
        const addPartnerCommissionRequest: AddPartnerCommissionRequest = {
            partnerName: this.partnerName ? this.partnerName : this.addPartner.value.partnerName,
            productIdForCommission: this.addPartner.value.productIdForCommission,
            commissionName: this.addPartner.value.partnerCommissionName,
            commissionPercentage: this.addPartner.value.partnerCommissionPercentage,
            commissionStartDate: this.addPartner.value.partnerCommissionStartDate
        };

        this._apiRequestService
            .sendRequest<
                AddPartnerCommissionRequest,
                Void
            >(this.storeBaseUrl, addPartnerCommissionRequest, true, this.addPartnerCommissionUri)
            .subscribe(() => {
                this._toastMessageService.displayToastMessage(
                    TOAST_STATE.success,
                    this._translateService.translate(SUCCESSFULLY_SAVED),
                    MAT_ICON.success
                );
                if (!this.newPartner) {
                    void this._router.navigateByUrl('/partners');
                }
            });
    }

    private _updatePartnerCommission(): void {
        const updatePartnerCommissionRequest: UpdatePartnerCommissionRequest = {
            id: this.editablePartnerCommissionId,
            partnerName: this.partnerName ? this.partnerName : this.addPartner.value.partnerName,
            productIdForCommission: this.addPartner.value.productIdForCommission,
            commissionName: this.addPartner.value.partnerCommissionName,
            commissionPercentage: this.addPartner.value.partnerCommissionPercentage,
            commissionStartDate: this.addPartner.value.partnerCommissionStartDate
        };

        this._apiRequestService
            .sendRequest<
                UpdatePartnerCommissionRequest,
                Void
            >(this.storeBaseUrl, updatePartnerCommissionRequest, true, this.updatePartnerCommissionUri)
            .subscribe(() => {
                this._toastMessageService.displayToastMessage(
                    TOAST_STATE.success,
                    this._translateService.translate(SUCCESSFULLY_MODIFIED),
                    MAT_ICON.success
                );
                void this._router.navigateByUrl('/partners');
            });
    }

    private _updatePartnerOnSalePeriod(): void {
        const updatePartnerOnSalePeriodRequest: UpdatePartnerOnSalePeriodRequest = {
            id: this.editablePartnerOnSalePeriodId,
            partnerName: this.partnerName ? this.partnerName : this.addPartner.value.partnerName,
            productIdForOnSalePeriod: this.addPartner.value.productIdForOnSalePeriod,
            onSalePeriodName: this.addPartner.value.partnerOnSalePeriodName,
            onSalePeriodPercentage: this.addPartner.value.partnerOnSalePeriodPercentage,
            onSalePeriodStartDate: this.addPartner.value.partnerOnSalePeriodStartDate,
            onSalePeriodEndDate: this.addPartner.value.partnerOnSalePeriodEndDate
        };

        this._apiRequestService
            .sendRequest<
                UpdatePartnerOnSalePeriodRequest,
                Void
            >(this.storeBaseUrl, updatePartnerOnSalePeriodRequest, true, this.updatePartnerOnSalePeriodUri)
            .subscribe(() => {
                this._toastMessageService.displayToastMessage(
                    TOAST_STATE.success,
                    this._translateService.translate(SUCCESSFULLY_MODIFIED),
                    MAT_ICON.success
                );
                void this._router.navigateByUrl('/partners');
            });
    }

    private _loadProducts(): void {
        this._apiRequestService.sendRequest(this.storeBaseUrl, {}, true, this.getProductsURI).subscribe((response) => {
            const products = response as GetProductsResponse;
            this.products = products.productList as Material[];
        });
    }

    private _setDefaultStartDate(): void {
        const startDate = new Date();
        this.defaultDate = `${startDate.getFullYear()}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}-${startDate
            .getDate()
            .toString()
            .padStart(2, '0')}`;
    }

    private _buildCommissionDataForEdit(): void {
        const storedCommission = localStorage.getItem('commission');
        if (storedCommission) {
            const commission = JSON.parse(storedCommission);

            this.editablePartnerCommissionId = commission.commissionId;
            this.partnerName = commission.partnerName;
            this.commissionNeeded = true;

            this.addPartner = this._fb.group({
                partnerName: [this.partnerName, Validators.required],
                productIdForCommission: [commission.productId],
                partnerCommissionName: [commission.name],
                partnerCommissionPercentage: [commission.percentage],
                partnerCommissionStartDate: [this._getformattedDate(commission.startDate), Validators.required],
                productIdForOnSalePeriod: [null],
                partnerOnSalePeriodName: [null],
                partnerOnSalePeriodPercentage: [null],
                partnerOnSalePeriodStartDate: [null],
                partnerOnSalePeriodEndDate: [null]
            });

            localStorage.removeItem('commission');
        }
    }

    private _buildOnSalePeriodDataForEdit(): void {
        const storedOnSalePeriod = localStorage.getItem('onSalePeriod');
        if (storedOnSalePeriod) {
            const onSalePeriod = JSON.parse(storedOnSalePeriod);

            this.editablePartnerOnSalePeriodId = onSalePeriod.onSalePeriodId;
            this.partnerName = onSalePeriod.partnerName;
            this.onSalePeriodNeeded = true;

            this.addPartner = this._fb.group({
                partnerName: [this.partnerName, Validators.required],
                productIdForCommission: [null],
                partnerCommissionName: [null],
                partnerCommissionPercentage: [null],
                partnerCommissionStartDate: [null],
                productIdForOnSalePeriod: [onSalePeriod.productId],
                partnerOnSalePeriodName: [onSalePeriod.partnerName, Validators.required],
                partnerOnSalePeriodPercentage: [onSalePeriod.percentage],
                partnerOnSalePeriodStartDate: [this._getformattedDate(onSalePeriod.startDate), Validators.required],
                partnerOnSalePeriodEndDate: [this._getformattedDate(onSalePeriod.endDate), Validators.required]
            });

            localStorage.removeItem('onSalePeriod');
        }
    }

    private _buildAddPartnerForm(): void {
        this.addPartner = this._fb.group({
            partnerName: [this.partnerName, Validators.required],
            productIdForCommission: [null],
            partnerCommissionName: [null],
            partnerCommissionPercentage: [null],
            partnerCommissionStartDate: [this.defaultDate, Validators.required],
            productIdForOnSalePeriod: [null],
            partnerOnSalePeriodName: [null, Validators.required],
            partnerOnSalePeriodPercentage: [null],
            partnerOnSalePeriodStartDate: [this.defaultDate, Validators.required],
            partnerOnSalePeriodEndDate: [this.defaultDate, Validators.required]
        });
    }

    private _launchFormWithSelectedMode(): void {
        const partnerName = this.addPartner.get('partnerName');
        if (partnerName?.value) {
            partnerName.disable();
            this.newPartner = false;
        }

        if (this.selectedMode === 'addCommission') {
            this.commissionNeeded = true;
        }

        if (this.selectedMode === 'addOnSalePeriod') {
            this.onSalePeriodNeeded = true;
        }
    }

    private _getformattedDate(date: string): string {
        const editableStartDate = new Date(this._dateUtilService.formatDate(date));
        const formattedDate = `${editableStartDate?.getFullYear()}-${(editableStartDate?.getMonth() + 1)
            .toString()
            .padStart(2, '0')}-${editableStartDate?.getDate().toString().padStart(2, '0')}`;

        return formattedDate;
    }
}
